import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { UserCircle2, KeyRound, Eye, EyeOff, ShieldCheck } from 'lucide-react'
import { authApi } from '@/api/auth'
import { useAuthStore } from '@/store/authStore'

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, '현재 비밀번호를 입력하세요.'),
    newPassword: z
      .string()
      .min(8, '새 비밀번호는 8자 이상이어야 합니다.')
      .max(100, '새 비밀번호는 100자 이하여야 합니다.'),
    confirmPassword: z.string().min(1, '새 비밀번호 확인을 입력하세요.'),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    path: ['confirmPassword'],
    message: '새 비밀번호가 일치하지 않습니다.',
  })
  .refine((v) => v.currentPassword !== v.newPassword, {
    path: ['newPassword'],
    message: '새 비밀번호는 현재 비밀번호와 달라야 합니다.',
  })

type PasswordForm = z.infer<typeof passwordSchema>

export function MyPage() {
  const { user } = useAuthStore()
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
  })

  const onSubmit = async (data: PasswordForm) => {
    setSubmitting(true)
    try {
      await authApi.changePassword(data.currentPassword, data.newPassword)
      toast.success('비밀번호가 변경되었습니다.')
      reset()
    } catch (error: any) {
      const message =
        error?.response?.data?.error?.message ||
        '비밀번호 변경에 실패했습니다. 현재 비밀번호를 확인해 주세요.'
      toast.error(message)
    } finally {
      setSubmitting(false)
    }
  }

  const roleLabel = user?.isAdminSuper
    ? '깨봉 Super Admin'
    : user?.adminRoleName || '관리자'

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">마이페이지</h1>
        <p className="mt-1 text-sm text-slate-500">
          내 계정 정보를 확인하고 비밀번호를 변경할 수 있습니다.
        </p>
      </div>

      {/* 프로필 카드 */}
      <section className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
            <UserCircle2 className="w-8 h-8 text-slate-900" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-lg text-slate-900 truncate">
              {user?.fullName || user?.email}
            </p>
            <p className="text-sm text-slate-500 truncate">{user?.email}</p>
          </div>
        </div>

        <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-slate-500">역할</dt>
            <dd className="mt-1 inline-flex items-center gap-1 text-slate-900 font-medium">
              <ShieldCheck className="w-4 h-4 text-amber-500" />
              {roleLabel}
            </dd>
          </div>
          <div>
            <dt className="text-slate-500">계정 상태</dt>
            <dd className="mt-1 text-slate-900 font-medium">
              {user?.isActive ? '활성' : '비활성'}
            </dd>
          </div>
          {user?.phoneNumber && (
            <div>
              <dt className="text-slate-500">전화번호</dt>
              <dd className="mt-1 text-slate-900 font-medium">{user.phoneNumber}</dd>
            </div>
          )}
          {user?.lastLoginAt && (
            <div>
              <dt className="text-slate-500">최근 로그인</dt>
              <dd className="mt-1 text-slate-900 font-medium">
                {new Date(user.lastLoginAt).toLocaleString('ko-KR')}
              </dd>
            </div>
          )}
        </dl>
      </section>

      {/* 비밀번호 변경 카드 */}
      <section className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="w-5 h-5 text-amber-500" />
          <h2 className="text-lg font-semibold text-slate-900">비밀번호 변경</h2>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <PasswordField
            label="현재 비밀번호"
            autoComplete="current-password"
            visible={showCurrent}
            onToggle={() => setShowCurrent((v) => !v)}
            error={errors.currentPassword?.message}
            {...register('currentPassword')}
          />
          <PasswordField
            label="새 비밀번호"
            autoComplete="new-password"
            visible={showNew}
            onToggle={() => setShowNew((v) => !v)}
            error={errors.newPassword?.message}
            hint="8자 이상 입력해 주세요."
            {...register('newPassword')}
          />
          <PasswordField
            label="새 비밀번호 확인"
            autoComplete="new-password"
            visible={showConfirm}
            onToggle={() => setShowConfirm((v) => !v)}
            error={errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />

          <div className="pt-2 flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? '변경 중...' : '비밀번호 변경'}
            </button>
          </div>
        </form>
      </section>
    </div>
  )
}

interface PasswordFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string
  visible: boolean
  onToggle: () => void
  error?: string
  hint?: string
}

const PasswordField = React.forwardRef<HTMLInputElement, PasswordFieldProps>(
  function PasswordField({ label, visible, onToggle, error, hint, ...rest }, ref) {
    return (
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          {label}
        </label>
        <div className="relative">
          <input
            ref={ref}
            type={visible ? 'text' : 'password'}
            className={`w-full px-3 py-2 pr-10 rounded-lg border bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 ${
              error ? 'border-red-400' : 'border-slate-300'
            }`}
            {...rest}
          />
          <button
            type="button"
            onClick={onToggle}
            tabIndex={-1}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
            aria-label={visible ? '비밀번호 숨기기' : '비밀번호 표시'}
          >
            {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {error ? (
          <p className="mt-1 text-xs text-red-500">{error}</p>
        ) : hint ? (
          <p className="mt-1 text-xs text-slate-500">{hint}</p>
        ) : null}
      </div>
    )
  }
)

export default MyPage
